import { RelationshipType } from '@/helpers/const';
import type { ApplicantWithRelationships } from '@/types';

/**
 * Resolves the married/de-facto partner id of the given applicant from their relationship rows.
 *
 * Matches the relationship in either direction (the applicant may be the row's `applicantId` or its
 * `relatedApplicantId`), so it works regardless of who "owns" the row. Crucially it does not depend
 * on a primary applicant existing, so it still pairs partners when both are secondaries — e.g. a
 * company/trust borrower application where the entity holds the primary role. Rows referencing
 * someone who is not on this loan (e.g. cross-application relationship rows) are ignored because the
 * resolved partner must be one of `applicants`.
 *
 * @param applicantId the applicant whose partner to resolve
 * @param applicants all applicants on the loan
 * @returns the partner's applicant id, or undefined when there is no married partner on the loan
 */
export function resolveMarriedPartnerId(
  applicantId: string | undefined,
  applicants: ApplicantWithRelationships[],
): string | undefined {
  if (!applicantId) return undefined;
  const applicantIds = new Set(applicants.map((a) => a.id));
  const me = applicants.find((a) => a.id === applicantId);
  return (me?.applicantRelationships ?? [])
    .filter((row) => row.relationship === RelationshipType.MARRIED_OR_DEFACTO)
    .map((row) => (row.applicantId === applicantId ? row.relatedApplicantId : row.applicantId))
    .find((id): id is string => id != null && id !== applicantId && applicantIds.has(id));
}
